/**
 * THESIS: Pro shop work is one continuous handoff from group tag to item rail
 * to receipt, not a generic retail dashboard or a modal maze.
 * OWN-WORLD: The Starter’s Tag Rack stays intact—score-paper panels, pine
 * rules, condensed times, and one Ball Signal checkout action.
 * STORY: Staff choose a checked-in, on-course, or completed group, build its
 * final tab, settle a synthetic payment, then reopen the receipt below.
 * FIRST VIEWPORT: Date control above a three-part register: group rail left,
 * course catalog center, pinned receipt right.
 * FORM: Register-and-receipt split, first of six task structures; direct
 * reference-led staging. No concept seed was run because the workflow and
 * established visual world were already specified.
 */
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Minus,
  PackagePlus,
  Plus,
  ReceiptText,
  Sandwich,
  ShoppingBasket,
  Tag,
  UsersRound,
} from "lucide-react"
import { useState } from "react"
import type { FormEvent } from "react"

import { StaffPagePending } from "@/components/loading-skeleton"
import { StatusChip } from "@/components/status-chip"
import { Button } from "@/components/ui/button"
import {
  formatCourseDate,
  formatCurrency,
  formatMinutes,
  formatTimestamp,
} from "@/lib/date"
import {
  PRO_SHOP_CATEGORIES,
  PRO_SHOP_PAYMENT_METHODS,
  calculateProShopTotal,
  proShopCategoryLabel,
  proShopPaymentLabel,
} from "@/lib/pro-shop"
import type { ProShopCategory, ProShopPaymentMethod } from "@/lib/pro-shop"
import {
  golfDeskQueryKeys,
  staffProShopQueryOptions,
} from "@/lib/query-options"
import { checkoutProShopFn } from "@/server/functions"

type ProShopSearch = {
  date?: string
  booking?: string
  page?: number
}

type CompletedReceipt = {
  id: string
  receiptNumber: string
  paymentMethod: ProShopPaymentMethod
  totalCents: number
}

const categoryIcons = {
  beverage: Coffee,
  food: Sandwich,
  add_on: Tag,
} satisfies Record<ProShopCategory, typeof Coffee>

const checkoutErrors: Record<string, string> = {
  BOOKING_NOT_FOUND:
    "That group is no longer available. Choose another group and try again.",
  GROUP_NOT_READY:
    "This group must be checked in, on course, or completed before checkout.",
  EMPTY_BASKET: "Add at least one item before completing checkout.",
  INVALID_QUANTITY:
    "One of the item quantities is too high. Reduce it and try again.",
  ITEM_UNAVAILABLE:
    "An item in this basket is no longer available. Refresh the tab and try again.",
  ACCOUNT_REQUIRED:
    "Guest bookings cannot be charged to an account. Choose cash or card terminal.",
  INVALID_TOTAL:
    "The basket total could not be verified. Review the items and try again.",
}

export const Route = createFileRoute("/staff/pro-shop")({
  validateSearch: (search: Record<string, unknown>): ProShopSearch => {
    const page =
      typeof search.page === "number"
        ? search.page
        : typeof search.page === "string"
          ? Number(search.page)
          : Number.NaN

    return {
      date: typeof search.date === "string" ? search.date : undefined,
      booking: typeof search.booking === "string" ? search.booking : undefined,
      page: Number.isInteger(page) && page > 1 ? page : undefined,
    }
  },
  loaderDeps: ({ search }) => ({
    date: search.date,
    page: search.page ?? 1,
  }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(
      staffProShopQueryOptions(deps.date, deps.page)
    ),
  head: () => ({
    meta: [{ title: "Pro shop · Groundline staff" }],
  }),
  pendingComponent: () => <StaffPagePending kind="shop" />,
  component: ProShop,
})

function ProShop() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { data } = useSuspenseQuery(
    staffProShopQueryOptions(search.date, search.page ?? 1)
  )
  const [category, setCategory] = useState<ProShopCategory>("beverage")
  const [basket, setBasket] = useState<Record<string, number>>({})
  const [paymentMethod, setPaymentMethod] =
    useState<ProShopPaymentMethod>("cash")
  const [completedReceipt, setCompletedReceipt] =
    useState<CompletedReceipt | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    crypto.randomUUID()
  )
  const selectedGroup =
    data.groups.find((group) => group.id === search.booking) ??
    data.groups.at(0) ??
    null
  const basketLines = data.catalog.flatMap((item) => {
    const quantity = basket[item.id] ?? 0

    return quantity > 0 ? [{ item, quantity }] : []
  })
  const basketTotal = calculateProShopTotal(
    basketLines.map((line) => ({
      quantity: line.quantity,
      unitPriceCents: line.item.priceCents,
    }))
  )
  const filteredCatalog = data.catalog.filter(
    (item) => item.category === category
  )
  const checkout = useMutation({
    mutationFn: (payload: {
      idempotencyKey: string
      bookingId: string
      paymentMethod: ProShopPaymentMethod
      items: Array<{ itemId: string; quantity: number }>
    }) => checkoutProShopFn({ data: payload }),
    onSuccess: async (result) => {
      if (!result.ok) {
        setCheckoutError(
          checkoutErrors[result.reason] ??
            "Checkout could not be completed. Review the tab and try again."
        )
        return
      }

      setCompletedReceipt(result.receipt)
      setCheckoutError(null)
      setBasket({})
      setIdempotencyKey(crypto.randomUUID())
      await queryClient.invalidateQueries({
        queryKey: golfDeskQueryKeys.staff(),
      })
    },
    onError: () => {
      setCheckoutError(
        "Checkout could not reach the demo register. Wait a moment and try again."
      )
    },
  })

  function beginNewCheckoutAttempt() {
    setIdempotencyKey(crypto.randomUUID())
  }

  function chooseGroup(groupId: string) {
    if (checkout.isPending) {
      return
    }

    const group = data.groups.find((candidate) => candidate.id === groupId)

    setBasket({})
    setCompletedReceipt(null)
    setCheckoutError(null)
    beginNewCheckoutAttempt()
    if (group?.isGuest && paymentMethod === "charge_to_account") {
      setPaymentMethod("cash")
    }
    void navigate({
      search: { date: data.date, booking: groupId, page: search.page },
      replace: true,
    })
  }

  function changeQuantity(itemId: string, amount: number) {
    if (checkout.isPending) {
      return
    }

    setCompletedReceipt(null)
    setCheckoutError(null)
    beginNewCheckoutAttempt()
    setBasket((current) => {
      const nextQuantity = Math.max(
        0,
        Math.min(20, (current[itemId] ?? 0) + amount)
      )
      const next = { ...current }

      if (nextQuantity === 0) {
        delete next[itemId]
      } else {
        next[itemId] = nextQuantity
      }

      return next
    })
  }

  function submitCheckout(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!selectedGroup || basketLines.length === 0) {
      setCheckoutError(
        selectedGroup
          ? checkoutErrors.EMPTY_BASKET
          : "Choose an eligible group before starting checkout."
      )
      return
    }

    setCheckoutError(null)
    checkout.mutate({
      idempotencyKey,
      bookingId: selectedGroup.id,
      paymentMethod,
      items: basketLines.map((line) => ({
        itemId: line.item.id,
        quantity: line.quantity,
      })),
    })
  }

  return (
    <>
      <header className="staff-page-head">
        <div>
          <span className="eyebrow">
            <ShoppingBasket className="size-4" />
            On-course checkout
          </span>
          <h1 className="staff-page-title">Settle the round.</h1>
          <p className="staff-page-copy">
            {formatCourseDate(data.date)} · {data.course.name}
          </p>
        </div>
        <div className="field min-w-[190px]">
          <label htmlFor="pro-shop-date">Operating date</label>
          <input
            id="pro-shop-date"
            className="control"
            type="date"
            min={data.firstDate}
            max={data.today}
            value={data.date}
            disabled={checkout.isPending}
            onChange={(event) => {
              setBasket({})
              setCompletedReceipt(null)
              setCheckoutError(null)
              beginNewCheckoutAttempt()
              void navigate({
                search: {
                  date: event.target.value,
                  booking: undefined,
                  page: undefined,
                },
              })
            }}
          />
        </div>
      </header>

      <div className="pro-shop-workspace">
        <section className="ruled-panel pro-shop-groups overflow-hidden">
          <div className="ruled-panel__head">
            <div>
              <span className="eyebrow">Group rail</span>
              <h2 className="mt-1 text-sm font-semibold">Ready to settle</h2>
            </div>
            <span className="text-xs font-semibold text-muted-foreground">
              {data.metrics.eligibleGroups}
            </span>
          </div>

          {data.groups.length === 0 ? (
            <div className="empty-state pro-shop-empty">
              <div>
                <UsersRound className="size-6" />
                <strong>No eligible groups</strong>
                <p className="mt-2">
                  Check a group in from Dispatch, or choose a recent date.
                </p>
              </div>
            </div>
          ) : (
            <div className="pro-shop-group-list">
              {data.groups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className="pro-shop-group"
                  data-selected={selectedGroup?.id === group.id || undefined}
                  aria-pressed={selectedGroup?.id === group.id}
                  disabled={checkout.isPending}
                  onClick={() => chooseGroup(group.id)}
                >
                  <span className="pro-shop-group__time">
                    {formatMinutes(group.startMinutes)}
                  </span>
                  <span className="min-w-0">
                    <strong>{group.leadPlayerName}</strong>
                    <small>
                      {group.playerCount}{" "}
                      {group.playerCount === 1 ? "player" : "players"} ·{" "}
                      {group.reference}
                      {group.isGuest ? " · Guest" : ""}
                    </small>
                  </span>
                  <StatusChip status={group.status} />
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="ruled-panel pro-shop-catalog overflow-hidden">
          <div className="ruled-panel__head">
            <div>
              <span className="eyebrow">Course catalog</span>
              <h2 className="mt-1 text-sm font-semibold">
                Food, drinks &amp; add-ons
              </h2>
            </div>
            <span className="text-[0.68rem] font-semibold text-muted-foreground">
              Synthetic demo prices
            </span>
          </div>

          <div className="pro-shop-category-tabs" aria-label="Catalog category">
            {PRO_SHOP_CATEGORIES.map((value) => {
              const Icon = categoryIcons[value]

              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={category === value}
                  data-selected={category === value || undefined}
                  disabled={checkout.isPending}
                  onClick={() => setCategory(value)}
                >
                  <Icon aria-hidden="true" />
                  {proShopCategoryLabel(value)}
                </button>
              )
            })}
          </div>

          <div className="pro-shop-item-list">
            {filteredCatalog.map((item) => {
              const quantity = basket[item.id] ?? 0

              return (
                <article className="pro-shop-item" key={item.id}>
                  <span className="min-w-0">
                    <strong>{item.name}</strong>
                    <small>{proShopCategoryLabel(item.category)}</small>
                  </span>
                  <span className="pro-shop-item__price">
                    {formatCurrency(item.priceCents, data.course.currency)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant={quantity > 0 ? "secondary" : "outline"}
                    aria-label={`Add ${item.name} to tab`}
                    disabled={checkout.isPending}
                    onClick={() => changeQuantity(item.id, 1)}
                  >
                    {quantity > 0 ? (
                      <>
                        <Check data-icon="inline-start" />
                        {quantity} on tab
                      </>
                    ) : (
                      <>
                        <Plus data-icon="inline-start" />
                        Add
                      </>
                    )}
                  </Button>
                </article>
              )
            })}
          </div>
        </section>

        <aside className="ruled-panel pro-shop-receipt overflow-hidden">
          <div className="ruled-panel__head">
            <div>
              <span className="eyebrow">
                <ReceiptText className="size-3.5" />
                Current tab
              </span>
              <h2 className="mt-1 text-sm font-semibold">
                {selectedGroup?.leadPlayerName ?? "Choose a group"}
              </h2>
            </div>
            <span className="text-xs font-semibold text-muted-foreground">
              {basketLines.reduce((sum, line) => sum + line.quantity, 0)} items
            </span>
          </div>

          <form
            className="pro-shop-receipt__body"
            aria-busy={checkout.isPending}
            onSubmit={submitCheckout}
          >
            {completedReceipt ? (
              <div className="action-success" role="status">
                <CheckCircle2 className="size-4 shrink-0" />
                <span>
                  <strong className="block">
                    {completedReceipt.receiptNumber}
                  </strong>
                  Settled{" "}
                  {formatCurrency(
                    completedReceipt.totalCents,
                    data.course.currency
                  )}{" "}
                  by {proShopPaymentLabel(completedReceipt.paymentMethod)}.
                </span>
              </div>
            ) : null}

            {selectedGroup ? (
              <dl className="pro-shop-receipt__group">
                <div>
                  <dt>Tee time</dt>
                  <dd>{formatMinutes(selectedGroup.startMinutes)}</dd>
                </div>
                <div>
                  <dt>Booking</dt>
                  <dd>{selectedGroup.reference}</dd>
                </div>
                <div>
                  <dt>Round</dt>
                  <dd>{selectedGroup.holes} holes</dd>
                </div>
              </dl>
            ) : null}

            {selectedGroup ? (
              <div className="pro-shop-roster">
                <span>
                  Group roster · {selectedGroup.playerCount}{" "}
                  {selectedGroup.playerCount === 1 ? "player" : "players"}
                </span>
                <strong>{selectedGroup.playerNames.join(" · ")}</strong>
              </div>
            ) : null}

            {basketLines.length === 0 ? (
              <div className="pro-shop-receipt__empty">
                <PackagePlus aria-hidden="true" />
                <strong>The tab is empty</strong>
                <p>
                  Choose a group, then add food, beverages, or course add-ons.
                </p>
              </div>
            ) : (
              <div className="pro-shop-receipt__lines">
                {basketLines.map((line) => (
                  <div className="pro-shop-receipt-line" key={line.item.id}>
                    <span className="min-w-0">
                      <strong>{line.item.name}</strong>
                      <small>
                        {formatCurrency(
                          line.item.priceCents,
                          data.course.currency
                        )}{" "}
                        each
                      </small>
                    </span>
                    <span
                      className="pro-shop-quantity"
                      aria-label={`${line.quantity} ${line.item.name}`}
                    >
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        aria-label={`Remove one ${line.item.name}`}
                        disabled={checkout.isPending}
                        onClick={() => changeQuantity(line.item.id, -1)}
                      >
                        <Minus />
                      </Button>
                      <strong>{line.quantity}</strong>
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        aria-label={`Add one ${line.item.name}`}
                        disabled={checkout.isPending || line.quantity >= 20}
                        onClick={() => changeQuantity(line.item.id, 1)}
                      >
                        <Plus />
                      </Button>
                    </span>
                    <strong className="pro-shop-receipt-line__total">
                      {formatCurrency(
                        line.item.priceCents * line.quantity,
                        data.course.currency
                      )}
                    </strong>
                  </div>
                ))}
              </div>
            )}

            <div className="pro-shop-receipt__total">
              <span>
                <small>Amount due</small>
                <strong>
                  {formatCurrency(basketTotal, data.course.currency)}
                </strong>
              </span>
              <small>No tax or service fee in this demo.</small>
            </div>

            <fieldset className="pro-shop-payment">
              <legend>Simulated payment method</legend>
              <div>
                {PRO_SHOP_PAYMENT_METHODS.map((method) => {
                  const disabled =
                    method === "charge_to_account" &&
                    selectedGroup?.isGuest === true

                  return (
                    <label
                      key={method}
                      data-selected={paymentMethod === method || undefined}
                      data-disabled={disabled || undefined}
                      title={
                        disabled
                          ? "Account charging requires a registered golfer"
                          : undefined
                      }
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        value={method}
                        checked={paymentMethod === method}
                        disabled={disabled || checkout.isPending}
                        onChange={() => {
                          setPaymentMethod(method)
                          setCompletedReceipt(null)
                          setCheckoutError(null)
                          beginNewCheckoutAttempt()
                        }}
                      />
                      <span>{proShopPaymentLabel(method)}</span>
                    </label>
                  )
                })}
              </div>
            </fieldset>

            {checkoutError ? (
              <p className="action-error" role="alert">
                {checkoutError}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="accent"
              className="w-full"
              disabled={
                !selectedGroup || basketLines.length === 0 || checkout.isPending
              }
            >
              <ReceiptText data-icon="inline-start" />
              {checkout.isPending
                ? "Completing checkout…"
                : "Complete checkout"}
            </Button>
            <p className="pro-shop-demo-note">
              Portfolio demo only. No card terminal or real payment is
              contacted.
            </p>
          </form>
        </aside>
      </div>

      <section className="ruled-panel pro-shop-history overflow-hidden">
        <div className="ruled-panel__head">
          <div>
            <span className="eyebrow">Transaction history</span>
            <h2 className="mt-1 text-sm font-semibold">
              Receipts for {formatCourseDate(data.date)}
            </h2>
          </div>
          <span className="pro-shop-history__summary">
            <strong>{data.metrics.receiptCount}</strong> settled ·{" "}
            <strong>
              {formatCurrency(data.metrics.settledCents, data.course.currency)}
            </strong>
          </span>
        </div>

        {data.transactions.length === 0 ? (
          <div className="empty-state pro-shop-empty">
            <div>
              <ReceiptText className="size-6" />
              <strong>No receipts for this date</strong>
              <p className="mt-2">
                Completed checkouts will be recorded here immediately.
              </p>
            </div>
          </div>
        ) : (
          <div>
            {data.transactions.map((transaction) => (
              <details
                className="rule-row pro-shop-transaction"
                key={transaction.id}
                id={`receipt-${transaction.id}`}
              >
                <summary>
                  <span>
                    <strong>{transaction.receiptNumber}</strong>
                    <small>
                      {formatTimestamp(
                        transaction.createdAt,
                        data.course.timezone
                      )}
                    </small>
                  </span>
                  <span>
                    <strong>{transaction.booking.leadPlayerName}</strong>
                    <small>
                      {formatMinutes(transaction.booking.startMinutes)} ·{" "}
                      {transaction.booking.reference}
                    </small>
                  </span>
                  <span>
                    <strong>
                      {formatCurrency(
                        transaction.totalCents,
                        data.course.currency
                      )}
                    </strong>
                    <small>
                      {proShopPaymentLabel(transaction.paymentMethod)}
                    </small>
                  </span>
                  <ChevronDown aria-hidden="true" />
                </summary>
                <div className="pro-shop-transaction__detail">
                  <div className="pro-shop-transaction__context">
                    <div className="pro-shop-transaction__meta">
                      <span>
                        <small>Staff</small>
                        <strong>{transaction.staffName}</strong>
                      </span>
                      <span>
                        <small>Players</small>
                        <strong>{transaction.booking.playerCount}</strong>
                      </span>
                      <span>
                        <small>Round</small>
                        <strong>{transaction.booking.holes} holes</strong>
                      </span>
                      <span>
                        <small>Status</small>
                        <strong className="capitalize">
                          {transaction.status}
                        </strong>
                      </span>
                    </div>
                    <p className="pro-shop-transaction__roster">
                      <small>Receipt roster</small>
                      <strong>
                        {transaction.booking.playerNames.join(" · ")}
                      </strong>
                    </p>
                  </div>
                  <div className="pro-shop-transaction__lines">
                    {transaction.items.map((item) => (
                      <div key={item.id}>
                        <span>
                          {item.quantity} × {item.name}
                        </span>
                        <strong>
                          {formatCurrency(
                            item.lineTotalCents,
                            data.course.currency
                          )}
                        </strong>
                      </div>
                    ))}
                    <div className="pro-shop-transaction__grand-total">
                      <span>Total</span>
                      <strong>
                        {formatCurrency(
                          transaction.totalCents,
                          data.course.currency
                        )}
                      </strong>
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>
        )}

        {data.pagination.totalTransactions > 0 ? (
          <footer className="pro-shop-history__pagination">
            <span>
              Showing{" "}
              {(data.pagination.page - 1) * data.pagination.pageSize + 1}–
              {Math.min(
                data.pagination.page * data.pagination.pageSize,
                data.pagination.totalTransactions
              )}{" "}
              of {data.pagination.totalTransactions}
            </span>
            <div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={data.pagination.page <= 1}
                onClick={() =>
                  void navigate({
                    search: {
                      date: data.date,
                      booking: search.booking,
                      page:
                        data.pagination.page - 1 > 1
                          ? data.pagination.page - 1
                          : undefined,
                    },
                  })
                }
              >
                <ChevronLeft data-icon="inline-start" />
                Previous
              </Button>
              <span>
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={data.pagination.page >= data.pagination.totalPages}
                onClick={() =>
                  void navigate({
                    search: {
                      date: data.date,
                      booking: search.booking,
                      page: data.pagination.page + 1,
                    },
                  })
                }
              >
                Next
                <ChevronRight data-icon="inline-end" />
              </Button>
            </div>
          </footer>
        ) : null}
      </section>
    </>
  )
}
